/**
 * Which products get a top-level sidebar item, and in what order.
 *
 * A pure function in a `.ts` beside the component rather than logic inside the `.tsx`: Node
 * cannot strip types from `.tsx`, so a filter living in `ProductNav.tsx` could not be imported by
 * a test. Taking the list as an argument keeps the only import type-only, so it is erased and
 * Node never resolves it — the same reason every product page keeps a `*Model.ts` beside it, and
 * the reason `submenuModel.ts` (which this replaces) existed.
 *
 * The nav is flat now: there is no "Apps" bucket, so every built product that lives inside this
 * viewer is its own first-class item. Two products are deliberately NOT their own item:
 *
 *   - Guide has no page (`surface.kind` `'none'`) — it is the floating dock, not a destination.
 *   - Live has no page of its own: it is folded into Discovery (`/discovery`). Deduping by
 *     surface path drops it, so the family never shows two items pointing at one page.
 */
import type { Product } from './products';

export function productNavItems(products: readonly Product[]): readonly Product[] {
  const seen = new Set<string>();
  const out: Product[] = [];
  for (const product of products) {
    if (product.status !== 'available' || product.surface.kind !== 'internal') continue;
    // Live shares Discovery's `/discovery`; the first product at a path wins, so Discovery keeps
    // it (it is declared first) and Live drops out rather than duplicating the destination.
    if (seen.has(product.surface.path)) continue;
    seen.add(product.surface.path);
    out.push(product);
  }
  return out;
}
